import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Card,
  Container,
  defaultFills,
  Grid,
  Heading,
  Icon,
  majorScale,
  Menu,
  MenuItem,
  PageContainer,
  PageHeader,
  Popover,
  Tag,
  useToast,
} from '../../npaper';
import { UserCard } from './UserCard';
import { useStores } from '../../stores';
import { observer } from 'mobx-react-lite';
import { InviteDialog } from './InviteDialog';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from 'ui-box';

export const UsersPage = () => {
  const history = useHistory();
  const [inviteUser, setInviteUser] = useState(false);

  return (
    <PageContainer>
      <InviteDialog isShown={inviteUser} onClose={() => setInviteUser(false)} />
      <Container>
        <Button
          iconLeft="arrow-left"
          marginBottom={majorScale(2)}
          onClick={() =>
            history.length > 0 ? history.goBack() : history.push('/settings')
          }
        >
          <FormattedMessage defaultMessage="Settings" />
        </Button>

        <PageHeader
          title="Brugere og rettigheder"
          actions={
            <Button iconRight="add" minimal onClick={() => setInviteUser(true)}>
              <FormattedMessage defaultMessage="Invite user" />
            </Button>
          }
        />
        <UsersSection />
      </Container>
    </PageContainer>
  );
};

const UsersSection = observer(() => {
  const intl = useIntl();
  const toaster = useToast();
  const { teamMemberStore, invitationStore } = useStores();

  useEffect(() => {
    teamMemberStore.getAll();
    invitationStore.getAll();
  }, [invitationStore, teamMemberStore]);

  return (
    <Grid>
      <div className="col-span-12 md:col-span-6 lg:col-span-4">
        {teamMemberStore.items.map((member, i) => (
          <UserCard
            key={member.id}
            member={member}
            marginBottom={majorScale(1)}
          />
        ))}
        <Heading
          size={300}
          marginTop={majorScale(4)}
          marginBottom={majorScale(1)}
        >
          Invitations
        </Heading>
        {invitationStore.items.length === 0 && (
          <p className="mt-1 muted">
            <FormattedMessage defaultMessage="No active inviations" />
          </p>
        )}
        {invitationStore.items.map((invitation, i) => (
          <Card
            key={invitation.id}
            padding={majorScale(1)}
            display="flex"
            alignItems="center"
            marginBottom={majorScale(1)}
          >
            <Box
              width={majorScale(4)}
              height={majorScale(4)}
              backgroundColor={defaultFills.normal[100]}
              borderRadius={majorScale(0.5)}
              display="flex"
              alignItems="center"
              justifyContent="center"
              marginRight={majorScale(1)}
            >
              <Icon icon="envelope" />
            </Box>
            <Box>
              <p>{invitation.email}</p>
              <p className="small muted">
                {invitation.key_expired && (
                  <Tag small>
                    <FormattedMessage defaultMessage="Expired" />
                  </Tag>
                )}
                <FormattedMessage
                  defaultMessage="Sent {date, date, long}"
                  values={{ date: invitation.created_at }}
                />
              </p>
            </Box>
            <Box marginLeft="auto" display="flex">
              <Popover
                content={(close) => (
                  <Menu>
                    <MenuItem
                      text={intl.formatMessage({
                        defaultMessage: 'Resend invite',
                      })}
                      icon="send-message"
                      onClick={() =>
                        invitationStore
                          .resendInvite(invitation)
                          .then(() =>
                            toaster.addToast({
                              message: intl.formatMessage({
                                defaultMessage: 'Invitation was sent',
                              }),
                              intent: 'success',
                            })
                          )
                          .catch(toaster.showDefaultError)
                      }
                    />
                    <MenuItem
                      text={intl.formatMessage({
                        defaultMessage: 'Delete invite',
                      })}
                      icon="trash"
                      intent="danger"
                      onClick={() =>
                        invitationStore
                          .deleteItem(invitation)
                          .then(() =>
                            toaster.addToast({
                              message: intl.formatMessage({
                                defaultMessage: 'Invitation has been deleted',
                              }),
                              intent: 'success',
                            })
                          )
                          .catch(toaster.showDefaultError)
                      }
                    />
                  </Menu>
                )}
              >
                <Button minimal iconLeft="more" />
              </Popover>
            </Box>
          </Card>
        ))}
      </div>
      <div className="col-span-12 md:col-span-6 lg:col-span-4">
        <Heading size={300}>
          <FormattedMessage defaultMessage="Roles" />
        </Heading>

        <p className="medium mt-4">
          <FormattedMessage defaultMessage="Admin" />
        </p>
        <p className="mb-4 muted">
          <FormattedMessage defaultMessage="Admins can create and update all resources and settings on your account. They cannot delete your account though." />
        </p>

        <p className="medium">
          <FormattedMessage defaultMessage="Sales" />
        </p>
        <p className="mb-4 muted">
          <FormattedMessage defaultMessage="Users with the Sales role can view all resources in your account, and manage sales, products and contacts. They cannot change your account settings or invite users." />
        </p>

        <p className="medium">
          <FormattedMessage defaultMessage="Purchasing" />
        </p>
        <p className="mb-4 muted">
          <FormattedMessage defaultMessage="Users with the Purchasing role can view all resources in your account, and manage purchase orders, products and contacts. They cannot change your account settings or invite users." />
        </p>

        <p className="medium">
          <FormattedMessage defaultMessage="Warehousing" />
        </p>
        <p className="mb-4 muted">
          <FormattedMessage defaultMessage="Users with the Warehousing role can view all resources in your account, and update stock. They cannot manage sales, purchase orders or contacts. They cannot change your account settings or invite users." />
        </p>
      </div>
    </Grid>
  );
});
